import React, { useEffect, useState } from "react";
import Navbar from '../../layouts/Navbar';
import Sidebar from '../../layouts/Sidebar';
import { Link, useNavigate } from "react-router-dom";
import Footer from '../../layouts/Footer';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';

const PackageDemands = () => {
    const baseUrl = "https://pastq.eddcodesgh.com/qApi/admin";
    const [packages, setPackages] = useState([]);
    const [filteredPackages, setFilteredPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const navigate = useNavigate();

    // Fetch package orders from API
    const fetchPackages = async () => {
        try {
            setLoading(true);
            
            const response = await axios.get(`${baseUrl}/package/_packages.php`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                },
                withCredentials: true
            });

            console.log("API Response:", response.data.data);
            
            if (response.data.status === 'success') {
                setPackages(response.data.data.packages || response.data.records || []);
                setFilteredPackages(response.data.data.packages || response.data.records || []);
            } else {
                toast.error(response.data.message || 'Failed to fetch package orders');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error loading package orders');
            // Set empty arrays on error
            setPackages([]);
            setFilteredPackages([]);
        } finally {
            setLoading(false);
        }
    };

    const formatAmount = (amount) => {
        if (!amount) return "N/A";
        return `GHS ${parseFloat(amount).toFixed(2)}`;
    };

    const formatStatus = (status) => {
        if (!status) return "N/A";
        
        let badgeClass = "badge bg-secondary";
        let displayText = status;
        
        switch(status.toLowerCase()) {
            case 'approved':
            case 'success':
            case 'paid':
                badgeClass = "badge bg-success";
                displayText = "Approved";
                break;
            case 'pending':
                badgeClass = "badge bg-warning";
                displayText = "Pending";
                break;
            case 'declined':
            case 'failed':
            case 'error':
                badgeClass = "badge bg-danger";
                displayText = "Declined";
                break;
            case 'refunded':
                badgeClass = "badge bg-info";
                displayText = "Refunded";
                break;
            default:
                badgeClass = "badge bg-secondary";
        }
        
        return <span className={badgeClass}>{displayText}</span>;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    // Handle search filter
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearch(value);
        
        if (value === "") {
            setFilteredPackages(packages);
        } else {
            const filtered = packages.filter(item => 
                (item.customer_name && item.customer_name.toLowerCase().includes(value)) ||
                (item.customer_email && item.customer_email.toLowerCase().includes(value)) ||
                (item.order_id && item.order_id.toLowerCase().includes(value)) ||
                (item.transaction_id && item.transaction_id.toLowerCase().includes(value)) ||
                (item.package_name && item.package_name.toLowerCase().includes(value)) ||
                (item.exam_level && item.exam_level.toLowerCase().includes(value))
            );
            setFilteredPackages(filtered);
        }
    };

    // Handle delete package order
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this package order?")) {
            try {
            
                const response = await axios.get(`${baseUrl}/package/_delete.php?id=${id}`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506`
                    },
                    withCredentials: true
                });
                
                if (response.data.status === 'success') {
                    toast.success(response.data.message || 'Package order deleted successfully');
                    fetchPackages();
                } else {
                    toast.error(response.data.message || 'Failed to delete package order');
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error deleting package order');
            }
        }
    };

    // Handle view package details
    const handleViewDetails = (id) => {
        navigate(`/admin/packages/view/${id}`);
    };

    // Handle package status update
    const handleUpdateStatus = async (id, newStatus) => {
        try {
            const response = await axios.put(`${baseUrl}/packages/_packages.php`, {
                id: id,
                payment_status: newStatus
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                },
                withCredentials: true
            });
            
            if (response.data.status === 'success') {
                toast.success(`Package status updated to ${newStatus}`);
                fetchPackages(); // Refresh the list
            } else {
                toast.error(response.data.message || 'Failed to update status');
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error.response?.data?.message || 'Error updating package status');
        }
    };

    // DataTable columns configuration
    const columns = [
        {
            name: 'Order ID',
            selector: row => row.order_id || 'N/A',
            sortable: true,
            cell: row => (
                <span className="text-primary fw-semibold">
                    {row.order_id || 'N/A'}
                </span>
            ),
            width: '180px'
        },
        {
            name: 'Customer',
            selector: row => row.customer_name || 'N/A',
            sortable: true,
            cell: row => (
                <div>
                    <strong>{row.customer_name || 'N/A'}</strong>
                    <br />
                    <small className="text-muted">{row.customer_email || 'N/A'}</small>
                </div>
            ),
            width: '200px'
        },
        {
            name: 'Package Details',
            cell: row => (
                <div>
                    <strong>{row.package_name || 'N/A'}</strong>
                    <br />
                    <small className="text-muted">
                        {row.exam_level || 'N/A'} - {row.exam_year || 'N/A'}
                    </small>
                </div>
            ),
            width: '180px'
        },
        {
            name: 'Phone',
            selector: row => row.customer_phone || 'N/A',
            sortable: true,
            width: '130px'
        },
        {
            name: 'Amount',
            selector: row => formatAmount(row.amount),
            sortable: true,
            width: '100px'
        },
        {
            name: 'Status',
            cell: row => formatStatus(row.payment_status),
            sortable: true,
            width: '120px'
        },
        {
            name: 'Date',
            selector: row => formatDate(row.created_at),
            sortable: true,
            width: '120px'
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="btn-group" role="group">
                    <button 
                        className="btn btn-sm btn-info"
                        onClick={() => handleViewDetails(row.id)}
                        title="View Details"
                    >
                        <i className="bi bi-eye"></i>
                    </button>
                    <button 
                        className="btn btn-sm btn-warning ms-1"
                        onClick={() => handleUpdateStatus(row.id, 'approved')}
                        title="Mark as Approved"
                        disabled={row.payment_status === 'approved'}
                    >
                        <i className="bi bi-check-circle"></i>
                    </button>
                    <button 
                        className="btn btn-sm btn-danger ms-1"
                        onClick={() => handleDelete(row.id)}
                        title="Delete"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            ),
            width: '150px'
        }
    ];

    // Custom table styles
    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f8f9fa',
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#495057',
                textTransform: 'uppercase'
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                padding: '12px 8px'
            },
        },
        rows: {
            style: {
                '&:hover': {
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    };

    // Custom loading component
    const CustomLoader = () => (
        <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading package orders...</p>
        </div>
    );

    // Custom no data component
    const CustomNoData = () => (
        <div className="text-center py-5">
            <i className="bi bi-package display-4 text-muted"></i>
            <h5 className="mt-3">No Package Orders Found</h5>
            <p className="text-muted">No exam package purchases have been made yet.</p>
            <button className="btn btn-primary mt-2" onClick={fetchPackages}>
                <i className="bi bi-arrow-repeat me-2"></i> Refresh
            </button>
        </div>
    );

    // Calculate revenue
    const calculateRevenue = () => {
        const approvedOrders = packages.filter(p => p.payment_status === 'approved');
        const totalRevenue = approvedOrders.reduce((sum, order) => {
            const amount = parseFloat(order.amount) || 27.00; 
            return sum + amount;
        }, 0);
        return totalRevenue.toFixed(2);
    };

    useEffect(() => {
        fetchPackages();
    }, []);

    return (
        <>
            <Navbar />
            <Sidebar />
            <main id="main" className="main">
                <div className="pagetitle">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card card-body shadow">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="mt-3 mb-0">Package Orders Management</h5>
                                        <nav>
                                            <ol className="breadcrumb mb-0">
                                                <li className="breadcrumb-item">
                                                    <Link to='/dashboard'>
                                                        <i className="bi bi-arrow-return-left me-1"></i> Dashboard
                                                    </Link>
                                                </li>
                                                <li className="breadcrumb-item active">Package Orders</li>
                                            </ol>
                                        </nav>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span className="badge bg-primary me-2">
                                            Total: {packages.length}
                                        </span>
                                        <span className="badge bg-success me-2">
                                            Approved: {packages.filter(p => p.payment_status === 'approved').length}
                                        </span>
                                        <span className="badge bg-warning">
                                            Pending: {packages.filter(p => p.payment_status === 'pending').length}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">All Package Orders</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <i className="bi bi-search"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by customer name, email, order ID, or package..."
                                                    value={search}
                                                    onChange={handleSearch}
                                                />
                                                {search && (
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        onClick={() => {
                                                            setSearch("");
                                                            setFilteredPackages(packages);
                                                        }}
                                                    >
                                                        <i className="bi bi-x"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <button 
                                                className="btn btn-primary me-2"
                                                onClick={fetchPackages}
                                                disabled={loading}
                                            >
                                                <i className="bi bi-arrow-clockwise me-1"></i>
                                                Refresh
                                            </button>
                                            
                                        </div>
                                    </div>
                                    
                                    {/* DataTable */}
                                    <DataTable
                                        columns={columns}
                                        data={filteredPackages}
                                        progressPending={loading}
                                        progressComponent={<CustomLoader />}
                                        customStyles={customStyles}
                                        pagination
                                        paginationPerPage={10}
                                        paginationRowsPerPageOptions={[5, 10, 20, 50]}
                                        highlightOnHover
                                        responsive
                                        noDataComponent={<CustomNoData />}
                                        subHeader
                                        subHeaderComponent={
                                            <div className="w-100 d-flex justify-content-between align-items-center mt-2">
                                                <small className="text-muted">
                                                    Showing {filteredPackages.length} of {packages.length} orders
                                                </small>
                                                <small className="text-muted">
                                                    Revenue: GHS {calculateRevenue()}
                                                </small>
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="row mt-4">
                        <div className="col-md-3">
                            <div className="card bg-primary text-white">
                                <div className="card-body">
                                    <h5 className="card-title">Total Orders</h5>
                                    <h2 className="display-6">{packages.length}</h2>
                                    <p className="card-text mb-0">All package purchases</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-success text-white">
                                <div className="card-body">
                                    <h5 className="card-title">Approved</h5>
                                    <h2 className="display-6">{packages.filter(p => p.payment_status === 'approved').length}</h2>
                                    <p className="card-text mb-0">Successful payments</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-warning text-white">
                                <div className="card-body">
                                    <h5 className="card-title">Pending</h5>
                                    <h2 className="display-6">{packages.filter(p => p.payment_status === 'pending').length}</h2>
                                    <p className="card-text mb-0">Awaiting payment</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-info text-white">
                                <div className="card-body">
                                    <h5 className="card-title">Revenue</h5>
                                    <h2 className="display-6">GHS {calculateRevenue()}</h2>
                                    <p className="card-text mb-0">Total revenue</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default PackageDemands;